import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import ContentText from '../Common/ContentText';
import './AncestrySelection.css';

interface Ancestry {
  id: string;
  name: string;
  description: string;
  averageHeight: string;
  averageLifespan: string;
  physicalTraits: string[];
  features: Array<{
    name: string;
    description: string;
    mechanics?: any;
  }>;
  tags: string[];
}

interface AncestrySelectionProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const AncestrySelection: React.FC<AncestrySelectionProps> = ({
  characterData,
  onDataChange,
}) => {
  const [ancestries, setAncestries] = useState<Ancestry[]>([]);
  const [selectedAncestry, setSelectedAncestry] = useState<Ancestry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAncestries = async () => {
      try {
        // Import the JSON file directly
        const ancestriesData = await import('../../data/ancestries.json');
        setAncestries(ancestriesData.default);
        
        // If character already has an ancestry selected, find and set it
        if (characterData.ancestry) {
          const found = ancestriesData.default.find((a: Ancestry) => a.id === characterData.ancestry);
          if (found) {
            setSelectedAncestry(found);
          }
        }
      } catch (err) {
        console.error('Failed to load ancestries:', err);
        setError('Failed to load ancestry data');
      } finally {
        setLoading(false);
      }
    };

    loadAncestries();
  }, [characterData.ancestry]);

  const handleAncestrySelect = (ancestry: Ancestry) => {
    setSelectedAncestry(ancestry);
    onDataChange({ ancestry: ancestry.id });
  };

  if (loading) {
    return <div className="ancestry-selection__loading">Loading ancestries...</div>;
  }

  if (error) {
    return <div className="ancestry-selection__error">{error}</div>;
  }

  return (
    <div className="ancestry-selection">
      <p className="ancestry-selection__description">
        Your ancestry determines your character's heritage and provides unique features and traits.
        Choose the ancestry that best fits your character concept.
      </p>

      <div className="ancestry-selection__content">
        {/* Ancestry List */}
        <div className="ancestry-selection__list">
          <h3>Available Ancestries</h3>
          <div className="ancestry-grid">
            {ancestries.map((ancestry) => (
              <div
                key={ancestry.id}
                className={`ancestry-card ${
                  selectedAncestry?.id === ancestry.id ? 'ancestry-card--selected' : ''
                }`}
                onClick={() => handleAncestrySelect(ancestry)}
              >
                <div className="ancestry-card__name">{ancestry.name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Ancestry Details */}
        {selectedAncestry && (
          <div className="ancestry-selection__details">
            <div className="ancestry-details">
              <h3>{selectedAncestry.name}</h3>
              <ContentText 
                content={selectedAncestry.description}
                className="ancestry-details__description"
              />

              <div className="ancestry-details__info">
                <div className="info-section">
                  <h4>Physical Traits</h4>
                  <ul>
                    {selectedAncestry.physicalTraits.map((trait, index) => (
                      <li key={index}>{trait}</li>
                    ))}
                  </ul>
                </div>

                <div className="info-section">
                  <h4>Ancestry Features</h4>
                  {selectedAncestry.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <h5>{feature.name}</h5>
                      <ContentText content={feature.description} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AncestrySelection;